// File: src/pages/Login.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Form,
  FormGroup,
} from "reactstrap";
import swal from "sweetalert";
import { FaLock, FaUser, FaEye, FaEyeSlash } from "react-icons/fa";

import api from "../import/axiosConfig";
import { Login_User, Login_OTP } from "../import/ApiEndPoint/Api";
import { ViewCompanyDetails } from "../import/ApiEndPoint/ApiCalling";

/* ================== Device key helper ================== */
// This key should be stored in browser localStorage on each authorised system,
// e.g. localStorage.setItem("LOCAL_DEVICE_KEY", "<your-activation-key>");
const LOCAL_DEVICE_KEY_STORAGE = "LOCAL_DEVICE_KEY";

const getDeviceKey = () => {
  try {
    return localStorage.getItem(LOCAL_DEVICE_KEY_STORAGE) || "";
  } catch {
    return "";
  }
};

/* ================== Financial Year Prefix/Suffix Helpers ================== */

const getFinancialYearLabel = (date = new Date()) => {
  const d = new Date(date);
  const month = d.getMonth(); // Jan = 0, Apr = 3
  const year = d.getFullYear();

  const startYear = month >= 3 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const getPreviousFinancialYearLabel = () => {
  const currentFY = getFinancialYearLabel();
  const startYear = Number(String(currentFY).split("-")[0]);
  return `${startYear - 1}-${String(startYear).slice(-2)}`;
};

const getFyStartYear = (fy) => {
  const n = Number(String(fy || "").split("-")[0]);
  return Number.isFinite(n) ? n : 0;
};

const normalizeInvoiceSeries = (company = {}) => {
  const currentFY = getFinancialYearLabel();
  const previousFY = getPreviousFinancialYearLabel();

  let raw =
    company?.invoiceSeries ||
    company?.invoicePrefixSuffix ||
    company?.prefixSuffixByFY ||
    [];

  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }

  let list = Array.isArray(raw) ? raw : [];

  list = list
    .map((x) => ({
      financialYear: x?.financialYear || x?.fy || "",
      Prefix: x?.Prefix ?? x?.prefix ?? "",
      Suffix: x?.Suffix ?? x?.suffix ?? "",
    }))
    .filter((x) => x.financialYear);

  // Backward compatibility for old company data
  if (!list.length && (company?.Prefix || company?.Suffix)) {
    list.push({
      financialYear: previousFY,
      Prefix: company?.Prefix || "",
      Suffix: company?.Suffix || "",
    });
  }

  // Always keep current FY row available
  if (!list.some((x) => x.financialYear === currentFY)) {
    list.push({
      financialYear: currentFY,
      Prefix: "",
      Suffix: "",
    });
  }

  return list.sort(
    (a, b) => getFyStartYear(a.financialYear) - getFyStartYear(b.financialYear),
  );
};

const normalizeCompanyDetailForLogin = (company = {}) => {
  const invoiceSeries = normalizeInvoiceSeries(company);
  const currentFY = getFinancialYearLabel();

  const currentInvoiceRow =
    invoiceSeries.find((x) => x.financialYear === currentFY) || {};

  return {
    ...company,

    // Full financial-year-wise prefix/suffix list
    invoiceSeries,

    // Keep old fields active for current FY, so existing invoice pages continue working
    Prefix: currentInvoiceRow?.Prefix || company?.Prefix || "",
    Suffix: currentInvoiceRow?.Suffix || company?.Suffix || "",

    // Extra active fields for future use
    activeFinancialYear: currentFY,
    activeInvoicePrefix: currentInvoiceRow?.Prefix || company?.Prefix || "",
    activeInvoiceSuffix: currentInvoiceRow?.Suffix || company?.Suffix || "",
  };
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  // Visible fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  // Hidden fields sent automatically
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [currentAddress, setCurrentAddress] = useState("");

  // OTP flow for MASTER
  const [needsOtp, setNeedsOtp] = useState(false);
  const [otp, setOtp] = useState("");

  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    try {
      const ud = JSON.parse(localStorage.getItem("userData") || "null");
      const tk = localStorage.getItem("token");

      if (ud && tk) {
        navigate(from, { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture geolocation
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      console.warn("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords || {};

        if (latitude != null && longitude != null) {
          setLatitude(String(latitude));
          setLongitude(String(longitude));
        }
      },
      (err) => {
        console.warn("Geolocation error in Login:", err?.message || err);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 1000,
      },
    );

    // If you have a way to compute currentAddress client-side, set it here.
    // setCurrentAddress("...");
  }, []);

  const persistSession = (user, allowedPermission) => {
    const token = user?.token;

    if (token) {
      localStorage.setItem("token", token);
    }

    localStorage.setItem("userData", JSON.stringify(user));

    if (allowedPermission) {
      localStorage.setItem(
        "allowedPermission",
        JSON.stringify(allowedPermission),
      );
    } else {
      localStorage.removeItem("allowedPermission");
    }

    try {
      window.dispatchEvent(new Event("userData:updated"));
    } catch {}
  };

  const storeCompanyDetails = async (user) => {
    try {
      if (!user?._id) return;

      const res = await ViewCompanyDetails(user?._id, user?.database);

      const company =
        res?.CompanyDetail ||
        res?.CompanyDetails ||
        res?.data?.CompanyDetail ||
        res?.data?.CompanyDetails ||
        null;

      if (company) {
        const normalizedCompany = normalizeCompanyDetailForLogin(company);

        localStorage.setItem(
          "Companydetail",
          JSON.stringify(normalizedCompany),
        );

        try {
          window.dispatchEvent(new Event("Companydetail:updated"));
        } catch {}
      }
    } catch (e) {
      // Keep login flow robust even if company detail fetch fails
      console.error("Failed to load Companydetail:", e?.message || e);
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    localStorage.removeItem("allowedPermission");

    const deviceKey = getDeviceKey();

    if (!needsOtp) {
      if (!email.trim()) {
        return swal("Required", "Please enter email.", "warning");
      }

      if (!password.trim()) {
        return swal("Required", "Please enter password.", "warning");
      }
    } else {
      if (!otp.trim()) {
        return swal("Required", "Please enter the OTP.", "warning");
      }
    }

    try {
      setLoading(true);

      if (!needsOtp) {
        const payload = {
          email,
          password,
          latitude,
          longitude,
          currentAddress,
        };

        const { data } = await api.post(Login_User, payload, {
          headers: {
            "x-device-key": deviceKey,
          },
        });

        if (data?.status && /otp send successfull/i.test(data?.message || "")) {
          setNeedsOtp(true);
          swal("OTP Sent", "Please check your email for the OTP.", "success");
          return;
        }

        if (data?.status && data?.user) {
          persistSession(data.user, data.allowedPermission);

          // Save company details including FY-wise invoice prefix/suffix
          await storeCompanyDetails(data.user);

          swal("Success", "Logged in successfully.", "success");
          navigate(from, { replace: true });
          return;
        }

        throw new Error(data?.message || "Login failed");
      }

      const payload = {
        email,
        otp,
      };

      const { data } = await api.post(Login_OTP, payload, {
        headers: {
          "x-device-key": deviceKey,
        },
      });

      if (data?.status && data?.user) {
        persistSession(data.user, data.allowedPermission);

        // Save company details including FY-wise invoice prefix/suffix
        await storeCompanyDetails(data.user);

        swal("Success", "Logged in successfully.", "success");
        navigate(from, { replace: true });
        return;
      }

      throw new Error(data?.message || "Invalid OTP");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to login. Please try again.";

      swal("Login failed", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={wrap}>
      <Card style={card}>
        <CardBody style={{ padding: 20 }}>
          <div style={logoWrap}>
            <img
              src={"/logomain.png"}
              alt="Logo"
              style={{
                width: 230,
                height: 100,
                borderRadius: 12,
                objectFit: "fill",
              }}
            />
          </div>

          <Form onSubmit={submit} style={{ marginTop: 8 }}>
            {!needsOtp ? (
              <>
                <FormGroup style={{ marginBottom: 12 }}>
                  <div style={inputWrap}>
                    <FaUser size={14} style={icon} />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={input}
                      autoComplete="username"
                    />
                  </div>
                </FormGroup>

                <FormGroup style={{ marginBottom: 12 }}>
                  <div style={inputWrap}>
                    <FaLock size={14} style={icon} />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={input}
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      style={toggle}
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                </FormGroup>

                <Button
                  type="submit"
                  color="primary"
                  style={btn}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" /> &nbsp; Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </>
            ) : (
              <>
                <FormGroup style={{ marginBottom: 12 }}>
                  <Input
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    style={{
                      height: 44,
                      background: "#fff",
                    }}
                    maxLength={6}
                  />
                </FormGroup>

                <Button
                  type="submit"
                  color="primary"
                  style={btn}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" /> &nbsp; Verifying...
                    </>
                  ) : (
                    "Verify OTP"
                  )}
                </Button>
              </>
            )}
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}

/* ---------- Centered, minimal styles ---------- */
const wrap = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(60rem 60rem at -10% -10%, #e9f2ff 0%, transparent 50%), radial-gradient(60rem 60rem at 110% 110%, #e9fff5 0%, transparent 50%), #f7f9fb",
  padding: 16,
  fontFamily: "Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
};

const card = {
  width: "100%",
  maxWidth: 400,
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  boxShadow: "0 12px 28px rgba(16,24,40,0.08)",
  background: "#fff",
};

const logoWrap = {
  display: "flex",
  justifyContent: "center",
  marginBottom: 6,
};

const inputWrap = {
  position: "relative",
  display: "flex",
  alignItems: "center",
};

const icon = {
  position: "absolute",
  left: 12,
  opacity: 0.6,
  top: 14,
};

const input = {
  paddingLeft: 36,
  height: 44,
  background: "#fff",
};

const toggle = {
  position: "absolute",
  right: 10,
  top: 8,
  bottom: 8,
  display: "grid",
  placeItems: "center",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  opacity: 0.7,
};

const btn = {
  width: "100%",
  height: 44,
  fontWeight: 700,
  letterSpacing: ".06em",
  textTransform: "uppercase",
};
